import type { CSSProperties } from 'react'
import { HELMET_COLUMN_COUNT, isUnknownTeam, resolveTecmoTeam } from '../constants/teams'
import './TeamHelmet.css'

type HelmetSize = 'sm' | 'md' | 'lg'

interface TeamHelmetProps {
  /** Team abbreviation as stored on the game, e.g. `RAMS`. */
  abbreviation?: string | null
  size?: HelmetSize
  className?: string
  /**
   * Set when the helmet stands alone. Left unset the helmet is decorative and
   * hidden from assistive tech, which is correct wherever the team name or
   * abbreviation already appears in text beside it.
   */
  label?: string
}

/**
 * Renders a single team's helmet from the shared sprite sheet.
 *
 * @returns `null` for an absent, Unknown, or unrecognized team, so callers can
 * fall back to plain text without needing their own guard. Unknown is a real
 * stored choice for results that never recorded a franchise — it has no sprite.
 *
 * @example
 * ```tsx
 * <TeamHelmet abbreviation={participant.teamAbbreviation} size="sm" />
 * ```
 */
export function TeamHelmet({
  abbreviation,
  size = 'md',
  className,
  label,
}: TeamHelmetProps) {
  const team = resolveTecmoTeam(abbreviation)

  if (!team) {
    return null
  }

  const style = {
    '--team-helmet-index': team.helmetIndex,
    '--team-helmet-columns': HELMET_COLUMN_COUNT,
  } as CSSProperties

  return (
    <span
      className={['team-helmet', `team-helmet--${size}`, className]
        .filter(Boolean)
        .join(' ')}
      style={style}
      title={team.name}
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
    />
  )
}

interface TeamHelmetLabelProps {
  abbreviation?: string | null
  size?: HelmetSize
  /** Hides the abbreviation text, leaving the helmet to speak for itself. */
  hideAbbreviation?: boolean
  className?: string
}

/**
 * A helmet paired with its abbreviation — the standard way a team is shown in
 * game results.
 *
 * @remarks
 * Degrades to the bare abbreviation when the team cannot be resolved, and to
 * "Unknown" when no franchise was recorded, so result rows never collapse.
 */
export function TeamHelmetLabel({
  abbreviation,
  size = 'sm',
  hideAbbreviation = false,
  className,
}: TeamHelmetLabelProps) {
  const team = resolveTecmoTeam(abbreviation)

  if (!team) {
    return (
      <span className="muted">{isUnknownTeam(abbreviation) ? 'Unknown' : abbreviation?.trim()}</span>
    )
  }

  return (
    <span
      className={['team-helmet-label', className].filter(Boolean).join(' ')}
      title={team.name}
    >
      <TeamHelmet abbreviation={team.abbreviation} size={size} />
      {!hideAbbreviation && (
        <span className="team-helmet-label__abbr">{team.abbreviation}</span>
      )}
    </span>
  )
}
